import React from 'react';
import type {
  GridApiPro,
  GridInitialState,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {extend} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {RowSelectionModel} from 'components/dataTable/types';
import type {Data, Schema} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import type {DataTableProps} from 'components/dataTable';
import type {TableConfiguration} from '../types';
import {
  TableConfigurationContext,
  useTableConfigurationContextProviderValue,
} from '../contexts/TableConfigurationContextType';
import {useTableSchema} from '../hooks/useTableSchema';
import {TableSchema} from './TableSchema';

extend(utc);

export type TableProps<TData extends Data> = {
  schema: Schema;
  data: TData[];
  loading?: boolean;
  onRowClick?: (row: TData) => void;
  header: React.ReactNode | string;
  configuration?: TableConfiguration;
  highlights?: string[];
  rowCount?: number;
  selection?: RowSelectionModel;
  onSelectionChanged?: (model: RowSelectionModel) => void;
  onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
  initialState?: GridInitialState;
  onSaveState?: (state: GridInitialState) => void;
  apiRef?: React.RefObject<GridApiPro | null>;
} & Pick<
  DataTableProps<TData>,
  'onPaginationModelChange' | 'onSortModelChange'
>;

export function Table<TData extends Data>({
  schema,
  data,
  loading,
  onRowClick,
  header,
  configuration,
  highlights,
  rowCount,
  selection,
  onSelectionChanged,
  onRowsUpdate,
  initialState,
  onSaveState,
  apiRef,
  onPaginationModelChange,
  onSortModelChange,
}: TableProps<TData>) {
  const {locale} = useLocalization();

  const tableContext = useTableConfigurationContextProviderValue(
    schema,
    configuration,
  );

  const tableSchema = useTableSchema(schema, tableContext.partValueRenderers);

  return (
    <TableConfigurationContext.Provider value={tableContext}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        <TableSchema
          header={header}
          schema={tableSchema}
          rows={data}
          loading={loading}
          onRowClick={onRowClick}
          highlights={highlights}
          rowCount={rowCount}
          selection={selection}
          onSelectionChanged={onSelectionChanged}
          onRowsUpdate={onRowsUpdate}
          initialState={initialState}
          onSaveState={onSaveState}
          apiRef={apiRef}
          onPaginationModelChange={onPaginationModelChange}
          onSortModelChange={onSortModelChange}
        />
      </LocalizationProvider>
    </TableConfigurationContext.Provider>
  );
}
